import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FiArrowLeft,
  FiCheckCircle,
  FiXCircle,
  FiTrendingUp,
  FiTrendingDown,
  FiAward,
  FiClock,
  FiDownload,
} from "react-icons/fi";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "react-toastify";
import api from "../services/api";

const InterviewResults = () => {
  const { interviewId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const contentRef = useRef(null);

  const fetchResults = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await api.get(`/interviews/${interviewId}/results`);
      setResults(response.data);
    } catch (err) {
      console.error("Error fetching results:", err);
      setError(
        err.response?.data?.message || "Error loading interview results"
      );
    } finally {
      setLoading(false);
    }
  }, [interviewId]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check premium status from backend
  useEffect(() => {
    const checkPremium = async () => {
      try {
        const response = await api.get("/subscriptions/premium/check");
        setIsPremium(response.data.isPremium);
      } catch (error) {
        console.error("Error checking premium status:", error);
        // Fallback to localStorage if API fails
        const user = JSON.parse(localStorage.getItem("user"));
        setIsPremium(user?.subscriptionStatus === "premium");
      }
    };
    checkPremium();
  }, []);

  // Detect language changes and reload results
  useEffect(() => {
    if (i18n.language !== currentLanguage && results) {
      console.log(`🌍 Language changed from ${currentLanguage} to ${i18n.language}, reloading results...`);
      setCurrentLanguage(i18n.language);
      fetchResults();
    }
  }, [i18n.language, currentLanguage, results, fetchResults]);

  const downloadPDF = async () => {
    if (!isPremium) {
      toast.warning(t("dashboard.needPremiumForReport") || "Premium required to download report");
      navigate("/subscription");
      return;
    }

    try {
      setIsDownloading(true);
      const element = contentRef.current;
      if (!element) return;

      // Importar html2canvas y jsPDF
      const html2canvas = require("html2canvas");
      const jsPDF = require("jspdf").jsPDF;

      // Obtener dimensiones del elemento
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff"
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.98);
      const imgWidth = 210; // A4 width en mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Crear PDF con dimensiones exactas del contenido
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [imgWidth, imgHeight]
      });

      pdf.addImage(imgData, "JPEG", 0, 0, imgWidth, imgHeight);
      pdf.save(`${results.interview.title}_report.pdf`);

      toast.success("¡Reporte descargado exitosamente!");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Error al descargar el reporte");
    } finally {
      setIsDownloading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 50) return "score-good";
    return "score-poor";
  };

  const getScoreBgColor = (score) => {
    if (score >= 50) return "score-bg-good";
    return "score-bg-poor";
  };

  const getDifficultyBadge = (difficulty) => {
    const normalized = difficulty.toLowerCase();
    return `badge-${normalized}`;
  };

  if (loading) {
    return (
      <div className="results-loading-container">
        <div className="results-loading-content">
          <div className="results-loading-spinner"></div>
          <p className="results-loading-text">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="results-error-container">
        <div className="results-error-content">
          <FiXCircle className="results-error-icon" />
          <p className="results-error-text">{error}</p>
          <button
            onClick={() => navigate("/dashboard")}
            className="btn btn-primary"
          >
            {t("results.backToInterviews")}
          </button>
        </div>
      </div>
    );
  }

  const { interview, results: interviewResults } = results;

  return (
    <div className="results-page-container">
      <div className="results-page-wrapper">
        {/* Header */}
        <div className="results-header results-header-sticky">
          <div className="results-header-actions">
            <button
              onClick={() => navigate("/dashboard")}
              className="btn btn-secondary"
            >
              <FiArrowLeft />
              {t("results.backToInterviews")}
            </button>
            <button
              onClick={downloadPDF}
              disabled={isDownloading}
              className="btn btn-primary"
              title={!isPremium ? "Premium required" : "Download report"}
            >
              <FiDownload /> {isDownloading ? "Descargando..." : "Descargar Reporte"}
            </button>
          </div>
          <h1 className="results-main-title">{t("results.title")}</h1>
          <p className="results-subtitle">{interview.title}</p>
        </div>

        {/* Main Content - Wrapped for PDF export */}
        <div ref={contentRef}>
          {/* Summary Cards */}
          <div className="results-summary-grid">
          <div className="summary-card">
            <div className="summary-card-header">
              <FiAward className="icon-2xl icon-info" />
              <span className="summary-card-label">
                {t("results.totalScore")}
              </span>
            </div>
            <p
              className={`summary-card-value ${getScoreColor(
                interviewResults.finalScore
              )}`}
            >
              {interviewResults.finalScore}/100
            </p>
          </div>

          <div className="summary-card">
            <div className="summary-card-header">
              <FiCheckCircle className="icon-2xl icon-success" />
              <span className="summary-card-label">
                {t("results.questionsAnswered")}
              </span>
            </div>
            <p className="summary-card-value">
              {interviewResults.answeredQuestions}/
              {interviewResults.totalQuestions}
            </p>
          </div>

          <div className="summary-card">
            <div className="summary-card-header">
              <FiXCircle className="icon-2xl icon-error" />
              <span className="summary-card-label">
                {t("interview.skipQuestion")}
              </span>
            </div>
            <p className="summary-card-value">
              {interviewResults.skippedQuestions}
            </p>
          </div>

          <div className="summary-card">
            <div className="summary-card-header">
              <FiClock className="icon-2xl icon-purple" />
              <span className="summary-card-label">
                {t("interview.duration")}
              </span>
            </div>
            <p className="summary-card-value">
              {Math.round(interview.duration / 60)}
              {t("results.minutes").charAt(0)}
            </p>
          </div>
        </div>

        {/* Overall Feedback */}
        {interviewResults.overallFeedback && (
          <div className="overall-performance">
            <h2 className="overall-performance-title">
              <div className="performance-icon-wrapper">
                <FiTrendingUp className="icon-white performance-icon" />
              </div>
              <span>{t("results.overallPerformance")}</span>
            </h2>
            <div className="markdown-content-prose">
              <div className="markdown-content">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({ node, children, ...props }) => (
                      <h1 className="markdown-h1" {...props}>{children}</h1>
                    ),
                    h2: ({ node, children, ...props }) => (
                      <h2 className="markdown-h2" {...props}>{children}</h2>
                    ),
                    h3: ({ node, children, ...props }) => (
                      <h3 className="markdown-h3" {...props}>{children}</h3>
                    ),
                    p: ({ node, ...props }) => (
                      <p className="markdown-p" {...props} />
                    ),
                    ul: ({ node, ...props }) => (
                      <ul className="markdown-ul" {...props} />
                    ),
                    ol: ({ node, ...props }) => (
                      <ol className="markdown-ol" {...props} />
                    ),
                    li: ({ node, ...props }) => (
                      <li className="markdown-li" {...props} />
                    ),
                    strong: ({ node, ...props }) => (
                      <strong className="markdown-strong" {...props} />
                    ),
                    em: ({ node, ...props }) => (
                      <em className="markdown-em" {...props} />
                    ),
                    blockquote: ({ node, ...props }) => (
                      <blockquote className="markdown-blockquote" {...props} />
                    ),
                    code: ({ node, inline, ...props }) =>
                      inline ? (
                        <code className="code-inline" {...props} />
                      ) : (
                        <code className="code-block" {...props} />
                      ),
                  }}
                >
                  {interviewResults.overallFeedback}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        )}

        {/* Question by Question Results */}
        <div className="results-feedback-card">
          <h2 className="results-feedback-title">
            {t("results.questionResults")}
          </h2>

          <div className="space-y-8">
            {interviewResults.questionResults.map((result, index) => (
              <div
                key={index}
                className={`result-card border-2 ${getScoreBgColor(
                  result.score
                )}`}
              >
                <div className="question-response-header">
                  <div className="question-response-info">
                    <div className="question-header">
                      <span className="badge badge-primary question-number">
                        {t("")} {index + 1}
                      </span>
                      <span
                        className={`badge ${getDifficultyBadge(
                          result.difficulty
                        )}`}
                      >
                        {t(
                          `results.${result.difficulty.toLowerCase()}`
                        ).toUpperCase()}
                      </span>
                      <span className="badge badge-category">
                        📁 {result.category}
                      </span>
                    </div>
                    <h3 className="question-title">{result.question}</h3>
                  </div>
                  <div className="score-display">
                    <p className={`score-value ${getScoreColor(result.score)}`}>
                      {result.score}
                    </p>
                    <p className="score-label">{t("results.outOf")} 100</p>
                    <div className="score-time">
                      <FiClock className="question-response-time-icon" />
                      <span>{result.duration}s</span>
                    </div>
                  </div>
                </div>

                {result.answer && (
                  <div className="feedback-answer mb-4">
                    <h4 className="feedback-header">
                      <FiCheckCircle />
                      {t("results.yourAnswer")}:
                    </h4>
                    <div className="question-response-text">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({ node, ...props }) => (
                            <p className="markdown-p" {...props} />
                          ),
                          code: ({ node, inline, ...props }) =>
                            inline ? (
                              <code className="code-inline-sky" {...props} />
                            ) : (
                              <code className="code-block-sky" {...props} />
                            ),
                        }}
                      >
                        {result.answer}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}

                {result.feedback && (
                  <div className="feedback-ai mb-4">
                    <h4 className="feedback-header">
                      <FiAward />
                      {t("results.aiFeedback")}:
                    </h4>
                    <div className="question-response-text leading-relaxed">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({ node, ...props }) => (
                            <p className="markdown-p" {...props} />
                          ),
                          ul: ({ node, ...props }) => (
                            <ul className="feedback-ul" {...props} />
                          ),
                          li: ({ node, ...props }) => (
                            <li className="markdown-li" {...props} />
                          ),
                          strong: ({ node, ...props }) => (
                            <strong
                              className="markdown-strong-indigo"
                              {...props}
                            />
                          ),
                          code: ({ node, inline, ...props }) =>
                            inline ? (
                              <code className="code-inline-indigo" {...props} />
                            ) : (
                              <code className="code-block-indigo" {...props} />
                            ),
                        }}
                      >
                        {result.feedback}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}

                {result.strengths && result.strengths.length > 0 && (
                  <div className="feedback-strengths mb-3">
                    <h4 className="feedback-header">
                      <div className="improvement-icon-badge-emerald">
                        <FiTrendingUp className="icon-white icon-sm" />
                      </div>
                      <span>{t("results.strengths")}</span>
                    </h4>
                    <ul className="space-y-2">
                      {result.strengths.map((strength, i) => (
                        <li
                          key={i}
                          className="strengths-improvements-container"
                        >
                          <span className="icon-success icon-lg mt-0-5">✓</span>
                          <span className="strengths-improvements-text">
                            {strength}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {result.areasForImprovement &&
                  result.areasForImprovement.length > 0 && (
                    <div className="feedback-improvements">
                      <h4 className="feedback-header">
                        <div className="improvement-icon-badge">
                          <FiTrendingDown className="icon-white icon-sm" />
                        </div>
                        <span>{t("results.areasForImprovement")}</span>
                      </h4>
                      <ul className="space-y-2">
                        {result.areasForImprovement.map((area, i) => (
                          <li
                            key={i}
                            className="strengths-improvements-container"
                          >
                            <span className="icon-info icon-lg mt-0-5">→</span>
                            <span className="strengths-improvements-text">
                              {area}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
              </div>
            ))}
          </div>
        </div>
        </div>
        {/* End of PDF content */}

        {/* Actions */}
        <div className="results-actions-container">
          <button
            onClick={() => navigate("/dashboard")}
            className="btn btn-secondary"
          >
            <FiArrowLeft />
            {t("results.backToInterviews")}
          </button>
          <button
            onClick={() => navigate("/interviews")}
            className="btn btn-primary"
          >
            {t("results.startNewInterview")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InterviewResults;
